import { faCalendarPlus, faUserPlus, faUsers } from '@fortawesome/pro-regular-svg-icons';
import { faFileExcel, faSpinner } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fab, TableCell } from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  ButtonBlue,
  ButtonBlueLight,
  ButtonFloating,
  FlexContainer,
  GenericListContainer,
  GenericListFilters,
  GenericTable,
  ReactTour,
  SkeletonTableSmall,
  SubtitlePage,
  TextError
} from 'components';
import { useEffectAfterRender, useInfiniteLoading, useModal } from 'hooks';
import { UserEntry } from 'models/list/UserList';
import React, { useCallback, useState } from 'react';
import Media from 'react-media';
import { useNavigate } from 'react-router-dom';
import { UsersService } from 'services';
import { UserListFilter } from './UserListFilter';
import { FilterItem } from 'utils/types/FilterItem';

import shortid from 'shortid';
import { FilterHelper, DocumentHelper, formatDate, translate } from 'utils/helpers';

const getListHeaders = () => [
  {
    name: 'fullName',
    label: translate('common.lastName'),
    template: row => (
      <TableCell key={shortid.generate()}>
        {(row.fullName && row.fullName.trim()) || '-'}
      </TableCell>
    )
  }, {
    name: 'email',
    label: translate('common.email'),
    template: row => (
      <TableCell key={shortid.generate()}>
        {row?.email || '-'}
      </TableCell>
    )
  }, {
    name: 'creationDate',
    label: translate('common.creationDate'),
    template: row => (
      <TableCell key={shortid.generate()}>
        {row.creationDate && (
          <span>
            <FontAwesomeIcon icon={faCalendarPlus} style={{ marginRight: '1rem' }} />
            {formatDate(row.creationDate)}
          </span>
          )}
      </TableCell>
    )
  }, {
    name: 'company',
    label: translate('common.company'),
    sortOn: 'label',
    template: row => (
      <TableCell key={shortid.generate()}>
        {row.companyName || '-'}
      </TableCell>
    )
  }, {
    name: 'accountActivated',
    label: (
      <>
        {translate('common.userAccount')}
        <br />
        {translate('pageUserList.hasDatabilanRole')}
      </>
    ),
    template: row => (
      <TableCell key={shortid.generate()}>
        {row.hasAccount ? translate('common.yes') : translate('common.no')}
        {(row.hasAccount && !row.accountActivated) && ` (${translate('common.deactivated')})`}
        <br />
        {row.userAccountApplications?.toUpperCase()?.includes('DATABILAN')
          ? translate('common.yes')
          : translate('common.no')}
      </TableCell>
    )
  }
];

export const UserList = () => {
  const navigate = useNavigate();
  const { open } = useModal();
  const filterKey = 'userList';

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<FilterItem<any>[]>(
    FilterHelper.getFilters(filterKey) || [
      {
        key: 'activeAndRole',
        booleanValue: true,
        label: translate('common.withDatabilanAccess')
      }
    ]);
  const [isExporting, setIsExporting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const getUserList = useCallback(
    (page, signal) =>
      UsersService.getUserList(
        FilterHelper.buildFilterForm(filters, search),
        page + 1, // Décalage pour l'API (éviter les doublons au premier scroll)
        signal
  ),[filters, search]);

  const {
    items,
    hasMore,
    isLoading,
    totalSize,
    reloadList,
    loadMore
  } = useInfiniteLoading(getUserList);

  useEffectAfterRender(() => {
    reloadList();
  }, [filters, search]);

  const handleRowClick = useCallback(row => navigate(`/utilisateurs/${row.personId}`), [navigate]);

  const displayModalCreateUser = useCallback(() => open({
    type: 'CREATE_USER',
    onConfirm: createdUserId => navigate(`/utilisateurs/${createdUserId}`),
    closeOnSubmit: true
  }), [navigate, open]);

  const exportUsersAccounts = useCallback(async () => {
    try {
      setIsExporting(true);

      // Appel au service existant
      const response = await UsersService.exportUsersAccounts(FilterHelper.buildFilterForm(filters, search));

      if (!response?.file) {
        throw new Error('Erreur lors de l’export');
      }

      // Création du lien de téléchargement
      const downloadLink = document.createElement('a');
      downloadLink.href = DocumentHelper.getExcelWithBase64(response);
      downloadLink.download = response.name || 'users.xlsx';
      downloadLink.click();
    } catch (error: any) {
      enqueueSnackbar(error?.message || translate('errors.export'), { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  }, [filters, search, enqueueSnackbar]);

  const renderButtonContainer = useCallback(() => (
   <>
    <br />
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1.5rem'}}>
      <div>
        <ButtonBlue
          data-cy="createNewUser"
          data-tour="step-admin-userCreate"
          onClick={displayModalCreateUser}
        >
          <FontAwesomeIcon icon={faUserPlus} />
          {translate('button.create')}
        </ButtonBlue>
      </div>
      <div>
        <ButtonBlueLight
          data-cy="exportUsers"
          onClick={exportUsersAccounts}
          disabled={isExporting || isLoading}
        >
          <FontAwesomeIcon icon={isExporting ? faSpinner : faFileExcel} spin={isExporting} />
          {'Export des utilisateurs'}
        </ButtonBlueLight>
      </div>
    </div>
   </>
  ), [displayModalCreateUser, exportUsersAccounts, isExporting, isLoading]);

  const renderButtonMobile = useCallback(() => (
    <ButtonFloating dataCy="createNewUser" onClick={displayModalCreateUser}>
      <Fab aria-label={translate('button.createUser')} color="primary">
        <FontAwesomeIcon icon={faUserPlus} size="lg" />
      </Fab>
    </ButtonFloating>
  ), [displayModalCreateUser]);

  return (
    <GenericListContainer>
      <SubtitlePage>
        {translate('pageUserList.title')}
      </SubtitlePage>
      {renderButtonContainer()}
      <GenericListFilters
        disabled={false}
        filterKey={filterKey}
        filters={filters}
        isVisible
        search={search}
        setFilters={setFilters}
        setSearch={setSearch}
        withSidePanel
        ComponentFilter={({ currentFilters, setCurrentFilters }) => (
          <UserListFilter
            currentFilters={currentFilters}
            setCurrentFilters={setCurrentFilters}
          />
        )}
      />

      <div data-tour="step-admin-userList">
        {!isLoading && items.length === 0
              ? (
                <FlexContainer alignItems="center" flexDirection="column">
                  <FontAwesomeIcon color="var(--grey)" icon={faUsers} size="3x" />
                  <TextError>{translate('errors.noUser')}</TextError>
                  <ButtonBlue data-cy="createNewUser" onClick={displayModalCreateUser}>
                    <FontAwesomeIcon icon={faUserPlus} />
                    {translate('button.createUser')}
                  </ButtonBlue>
                </FlexContainer>
              ) : (
                <GenericTable<UserEntry>
                      dataCy="userList"
                      hasMore={hasMore}
                      headers={getListHeaders()}
                      loadMore={loadMore}
                      rows={items}
                      total={totalSize}
                      onRowClick={handleRowClick}
                  />
              )
          }
      </div>

      {isLoading && <SkeletonTableSmall />}

      <Media
            query="(max-width: 1080px)"
            render={renderButtonMobile}
        />

      <ReactTour steps={[]} />

    </GenericListContainer>
  );
};
